/**
 * Container for a single editable extraction line-item row.
 *
 * Pre-binds the per-row `(category, index, text)` arguments onto the
 * `onStartEdit` / `onRequestDelete` callbacks so the View receives
 * stable, zero-argument handlers and can satisfy the
 * `react-perf/jsx-no-new-function-as-prop` lint rule. All other state
 * (editing flag, error messages, draft text) is owned by the parent
 * `ExtractionDataSectionContainer` and threaded through.
 * @module features/contracts/components/LineItemRow/LineItemRowContainer
 */

import React, { memo, useCallback } from "react";

import LineItemRowView from "./LineItemRowView";

/** Props for {@link LineItemRowContainer}. */
export interface LineItemRowContainerProps {
  /** Category key the row belongs to. */
  readonly category: string;
  /** Zero-based index within the category's items array. */
  readonly index: number;
  /** Display text of the row when not editing. */
  readonly text: string;
  /** Whether the AI flagged this item as a conflict. */
  readonly isConflict: boolean;
  /** Whether this row was edited or added since the last refetch. */
  readonly isRecentlyModified: boolean;
  /** Whether the row is currently in inline-edit mode. */
  readonly isBeingEdited: boolean;
  /** Working draft text shown in the inline edit input (only used when `isBeingEdited`). */
  readonly draftText: string;
  /** Whether the edit mutation is currently in flight. */
  readonly isEditingMutation: boolean;
  /** Per-row edit error message, or null. */
  readonly editErrorMessage: string | null;
  /** Per-row delete error message, or null. */
  readonly deleteErrorMessage: string | null;
  /** Parent handler for entering edit mode. */
  readonly onStartEdit: (
    category: string,
    index: number,
    currentText: string
  ) => void;
  /** Parent handler for changing the inline-edit draft text. */
  readonly onChangeEditDraft: (text: string) => void;
  /** Parent handler for saving the inline edit. */
  readonly onSaveEdit: () => void;
  /** Parent handler for cancelling the inline edit. */
  readonly onCancelEdit: () => void;
  /** Parent handler for opening the delete confirmation dialog. */
  readonly onRequestDelete: (category: string, index: number) => void;
  /**
   * When `true`, suppresses the inline-edit affordances (pencil/trash
   * buttons, text press-to-edit, inline edit input). The row still
   * renders its conflict / recently-modified presentation so the data
   * stays legible in read-only contexts (e.g. a SAVED extraction).
   */
  readonly readOnly?: boolean;
}

/**
 * Pre-binds parent callbacks with `(category, index, text)` so the
 * View can pass them as bare `onPress` handlers without inline arrows.
 * @param props - Container props
 * @returns The rendered row view
 */
function LineItemRowContainer(props: LineItemRowContainerProps) {
  const {
    category,
    index,
    text,
    isConflict,
    isRecentlyModified,
    isBeingEdited,
    draftText,
    isEditingMutation,
    editErrorMessage,
    deleteErrorMessage,
    onStartEdit,
    onChangeEditDraft,
    onSaveEdit,
    onCancelEdit,
    onRequestDelete,
    readOnly = false,
  } = props;

  const handlePressEdit = useCallback(() => {
    onStartEdit(category, index, text);
  }, [onStartEdit, category, index, text]);

  const handlePressDelete = useCallback(() => {
    onRequestDelete(category, index);
  }, [onRequestDelete, category, index]);

  return (
    <LineItemRowView
      category={category}
      index={index}
      text={text}
      isConflict={isConflict}
      isRecentlyModified={isRecentlyModified}
      isBeingEdited={isBeingEdited}
      draftText={draftText}
      isEditingMutation={isEditingMutation}
      editErrorMessage={editErrorMessage}
      deleteErrorMessage={deleteErrorMessage}
      onPressEdit={handlePressEdit}
      onChangeEditDraft={onChangeEditDraft}
      onSaveEdit={onSaveEdit}
      onCancelEdit={onCancelEdit}
      onPressDelete={handlePressDelete}
      readOnly={readOnly}
    />
  );
}

LineItemRowContainer.displayName = "LineItemRowContainer";

export default memo(LineItemRowContainer);
